import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AuthShell } from "@/components/auth/AuthShell";
import { useAppAuth } from "@/components/auth/AuthProvider";
import { getAuthErrorMessage } from "@/components/auth/auth-helpers";
import styles from "@/components/auth/AuthForm.module.scss";

export function ResetPasswordPage() {
  const { isLoaded, finishPasswordReset } = useAppAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const token = params.get("token");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token) return;
    setError("");
    setIsSubmitting(true);
    try {
      await finishPasswordReset(token, password);
      navigate("/app", { replace: true });
    } catch (submitError) {
      setError(getAuthErrorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthShell mode="reset-password">
      <div className={styles.stack}>
        {!token ? <p className={styles.error}>This reset link is missing or invalid.</p> : null}
        {error ? <p className={styles.error}>{error}</p> : null}
        {token ? (
          <form className={styles.form} onSubmit={handleSubmit}>
            <label className={styles.field}>
              <span className={styles.label}>New password</span>
              <input
                className={styles.input}
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={!isLoaded || isSubmitting}
                required
              />
            </label>
            <button className={styles.submit} type="submit" disabled={!isLoaded || isSubmitting}>
              {isSubmitting ? <Loader2 size={20} className={styles.spinIcon} /> : "Set new password"}
            </button>
          </form>
        ) : null}
        <div className={styles.linkRow}>
          <Link className={styles.link} to="/login">Back to sign in</Link>
        </div>
      </div>
    </AuthShell>
  );
}
